import * as yup from 'yup'

import { addressSchema } from '@/schemas'

export const accountSchema = yup.object({
  company: yup.string().required('Company is required'),
  first_name: yup.string().required('Domain is required'),
  last_name: yup.string().required('Last name is required'),
  email: yup.string().required('Email is required'),
  phone: yup.string().required('Phone is required'),
  address: addressSchema.required('Address is required'),
})

const cognitoCustomAttributeSchema = yup.object({
  name: yup.string().required('Custom attribute name is required'),
  mutable: yup.boolean().required('Mutable is required'),
  minLen: yup.number().optional(),
  maxLen: yup.number().optional(),
})

const cognitoRoleSchema = yup.object({
  roleName: yup.string().required('Role name is required'),
  description: yup.string().optional(),
})

const cognitoConfigSchema = yup.object({
  customAttributes: yup.array().of(cognitoCustomAttributeSchema).optional().default([]),
  defaultAttributes: yup
    .array()
    .of(yup.string().required('Default attribute must be a string'))
    .optional()
    .default([]),
  roles: yup.array().of(cognitoRoleSchema).optional().default([]),
})

const emailFromRegex = /^([^<>]+)<([^<>]+)>$/
const emailRegex = /^[\w.%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,}$/

const cognitoSesSchema = yup.object().shape({
  enabled: yup.boolean().default(false),
  identity: yup
    .object()
    .when('enabled', ([enabled], schema) => {
      if (!enabled) return schema
      return schema.shape({
        type: yup.string().required('Identity type is required').default(''),
        value: yup
          .string()
          .when('type', ([type], yup) => {
            if (type === 'DOMAIN')
              return yup
                .required('Domain is required')
                .matches(domainRegex, 'Invalid domain format')
            if (type === 'EMAIL')
              return yup.required('Email is required').matches(emailRegex, 'Invalid email format')
            return yup.required('Identity Value is required')
          })
          .default(''),
      })
    })
    .shape({ type: yup.string().default(''), value: yup.string().default('') }),
  email: yup
    .object()
    .when(['enabled', 'identity'], ([enabled, identity], schema) => {
      if (!enabled) return schema
      return schema.shape({
        from: yup
          .string()
          .required('Email From is required')
          .matches(emailFromRegex, "Doesn't match the required format")
          .test('match-email', 'Must match identity email', (value, context) => {
            const match = value.match(emailFromRegex)
            if (!match) return false

            const [, , email] = match

            if (!emailRegex.test(email)) {
              return context.createError({
                message: 'Invalid email format',
                path: 'ses.email.from',
              })
            }

            if (identity.type === 'EMAIL') return email === identity.value

            if (email.split('@')[1] !== identity.value) {
              return context.createError({
                message: 'Domain must match identity value',
                path: 'ses.email.from',
              })
            }
            return true
          })
          .default(''),
        replyTo: yup
          .string()
          .required('Email Reply To is required ')
          .matches(emailRegex, 'Invalid email format')
          .default(''),
      })
    })
    .shape({
      from: yup.string().default(''),
      replyTo: yup.string().default(''),
    }),
})

// Utility function for creating object schemas with dynamic keys
const dynamicObjectSchema = <T extends yup.AnySchema>(schema: T) =>
  yup.object().test('is-valid-object', 'Invalid object structure', (value) => {
    if (typeof value !== 'object' || value === null) return false
    return Object.values(value).every((v) => schema.isValidSync(v))
  })

const lambdaMonitorConfigSchema = yup.object({
  enabled: yup.boolean().required(),
  minInvocationsCount: yup.number().optional(),
  maxFaultCount: yup.number().optional(),
  maxLatencyMillisecond: yup.number().optional(),
})

const dynamodbMonitorConfigSchema = yup.object({
  enabled: yup.boolean().required(),
  maxErrorCount: yup.number().optional(),
  writeThrottledEventsCount: yup.number().optional(),
})

const monitorConfigSchema = yup.object({
  lambdas: dynamicObjectSchema(lambdaMonitorConfigSchema).default({}),
  dynamodb: dynamicObjectSchema(dynamodbMonitorConfigSchema).default({}),
})

export const backendResourcesSchema = yup.object({
  id: yup.string().required('Group ID is required'),
  created: yup.string().required('Creation date is required'),
  updated: yup.string().required('Update date is required'),
  workflowStatus: yup.string().required('Workflow status is required'),
  workflowId: yup.string().default(''),
  stage: yup.string().optional(),
  accountId: yup.string().required('Account ID is required'),
  groupId: yup.string().required('Group ID is required'),
  name: yup.string().required('Name is required'),
  description: yup.string().required('Description is required'),
  lambdaAlgoliaSrcKey: yup.string().required('Lambda Algolia source key is required'),
  cognitoLambdaTriggerSrcKey: yup
    .string()
    .required('Cognito Lambda trigger source key is required'),
  artifactsBucketName: yup.string().optional(),
  apiLambdaSrcKey: yup.string().optional(),
  apiLambdaVersion: yup.string().optional(),
  outputs: yup.string().optional(),
  cognitoConfig: yup
    .string()
    .test('is-valid-cognito-config', 'Must be a valid cognito configuration', (value) => {
      if (!value) return true
      try {
        const parsedValue = JSON.parse(value)
        return cognitoConfigSchema.isValidSync(parsedValue)
      } catch (error) {
        return false
      }
    }),
  ses: cognitoSesSchema.optional(),
  monitor: yup
    .string()
    .test('is-valid-monitor-config', 'Must be a valid monitoring configuration', (value) => {
      if (!value) return true
      try {
        const parsedValue = JSON.parse(value)
        return monitorConfigSchema.isValidSync(parsedValue)
      } catch (error) {
        return false
      }
    }),
})
